// Client messages of the lobby panel: host commands and the team pick. Pure builders; the session sends them.

import { create } from "@bufbuild/protobuf";
import type { GameMode } from "../proto/gunrun/v1/common_pb";
import { Team } from "../proto/gunrun/v1/common_pb";
import type { BotLevel } from "../hud/types";
import { botDifficulty } from "./convert";
import {
  AddBotSchema,
  ClientMessageSchema,
  HostCommandSchema,
  RoomSettingsSchema,
  SelectTeamSchema,
  StartMatchSchema,
} from "../proto/gunrun/v1/game_pb";
import type { ClientMessage, HostCommand } from "../proto/gunrun/v1/game_pb";
import type { LobbyRules, LobbyRulesPatch } from "./lobby";

type HostCmd = NonNullable<HostCommand["cmd"]>;

function host(cmd: HostCmd): ClientMessage {
  return create(ClientMessageSchema, { msg: { case: "host", value: create(HostCommandSchema, { cmd }) } });
}

export function startMatchMessage(): ClientMessage {
  return host({ case: "startMatch", value: create(StartMatchSchema) });
}

export function setMapMessage(map: string): ClientMessage {
  return host({ case: "setMap", value: map });
}

export function setModeMessage(mode: GameMode): ClientMessage {
  return host({ case: "setMode", value: mode });
}

/**
 * The server replaces name, listing and limits from the full `RoomSettings`, so the patch goes on top of the
 * current rules (a field left alone is sent back unchanged and the server sees no change). Mode and map are
 * left empty: they have their own commands.
 */
export function setRulesMessage(current: LobbyRules, patch: LobbyRulesPatch): ClientMessage {
  const rules = { ...current, ...patch };
  return host({
    case: "setSettings",
    value: create(RoomSettingsSchema, {
      name: rules.name,
      maxPlayers: rules.maxPlayers,
      scoreLimit: rules.scoreLimit,
      timeLimitSec: rules.timeLimitSec,
      roundsToWin: rules.roundsToWin,
      friendlyFire: rules.friendlyFire,
      listed: rules.listed,
      hasPassword: rules.hasPassword,
    }),
  });
}

export function kickMessage(playerId: number): ClientMessage {
  return host({ case: "kick", value: playerId });
}

/** Adds a bot to `team` (free for all ignores it) at the given difficulty. */
export function addBotMessage(team: "a" | "b" | "none", level: BotLevel): ClientMessage {
  const t = team === "a" ? Team.A : team === "b" ? Team.B : Team.NONE;
  return host({ case: "addBot", value: create(AddBotSchema, { team: t, difficulty: botDifficulty(level) }) });
}

/** Removes the bot with this player id. */
export function removeBotMessage(playerId: number): ClientMessage {
  return host({ case: "removeBot", value: playerId });
}

export function selectTeamMessage(team: "a" | "b"): ClientMessage {
  return create(ClientMessageSchema, {
    msg: { case: "selectTeam", value: create(SelectTeamSchema, { team: team === "a" ? Team.A : Team.B }) },
  });
}
